import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { BrandIconName, Form, FormSchemaEntry, Input, SvgIcon, validateForm, ValidationErrors } from '@gik/ui';
import { CreatePageContext } from '@gik/create/components/CreatePage/CreatePage';
import noop from '@gik/core/utils/noop';
import { usePrevious } from 'react-use';
import { PayPalFormInputNames } from '@gik/create/enums/PayPalFormInputNames';
import { GoFundMeFormInputNames } from '@gik/create/enums/GoFundMeFormInputNames';

export enum DonationProviderType {
  PayPal = 'paypal',
  GoFundMe = 'gofundme',
}

export interface IDonationProviderProps {
  schema: FormSchemaEntry[];
  provider: DonationProviderType;
  icon: BrandIconName;
  target: string;
  description: string;
  className: string;
  value: DonationFormValueType;
  onValueChange: (value: DonationFormValueType) => void;
  asForm?: boolean;
  trackingId?: string;
  recalculateHeightCallback?: () => void;
}

export type PayPalDonationFormValueType = {
  [PayPalFormInputNames.EmailAddress]: string;
  isPayPalValueValid: boolean;
};

export type GoFundMeDonationFormValueType = {
  [GoFundMeFormInputNames.PageURL]: string;
  isGoFundMeValueValid: boolean;
};

export type DonationFormValueType = PayPalDonationFormValueType | GoFundMeDonationFormValueType;

const blockName = 'donation-provider';

export default function DonationProvider({
  schema,
  provider,
  icon,
  target,
  description,
  className,
  value,
  onValueChange,
  asForm = false,
  trackingId,
  recalculateHeightCallback = noop,
}: IDonationProviderProps) {
  const bem = bemBlock(blockName);
  const ctx = React.useContext(CreatePageContext);

  // validation for onChange
  const [hasBlurred, setHasBlurred] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>(null);

  const prevHasBlurred = usePrevious(hasBlurred);

  React.useEffect(() => {
    if (prevHasBlurred !== hasBlurred && error) {
      recalculateHeightCallback?.();
    }
  }, [recalculateHeightCallback, prevHasBlurred, hasBlurred, error]);

  const customValidateForm = (values: DonationFormValueType, fieldName: string): boolean => {
    const errors: ValidationErrors = validateForm(schema, values);
    const payPalOrGofundmeFieldErrorMessage = errors?.[fieldName]?.message;
    setError(payPalOrGofundmeFieldErrorMessage);
    return !payPalOrGofundmeFieldErrorMessage;
  };

  return (
    <div className={bem(null, [{ [provider]: provider }], className)}>
      <SvgIcon className={bem('icon')} name={icon} set={'brand-icons'} />
      <p className={bem('target')}>{target}:</p>
      {asForm ? (
        <Form
          ref={ref => {
            if (ctx) {
              switch (provider) {
                case DonationProviderType.GoFundMe:
                  ctx.donationsFormAsideGoFundMeRef.current = ref;
                  break;
                case DonationProviderType.PayPal:
                  ctx.donationsFormAsidePayPalRef.current = ref;
                  break;
              }
            }
          }}
          schema={schema}
          onChange={onValueChange}
          initialValues={value}
          trackingId={trackingId}
        />
      ) : (
        <>
          {schema.map(schemaItem => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { customValidation: _, ...field } = schemaItem;
            const fieldName = field.name;

            return (
              <React.Fragment key={fieldName}>
                <Input
                  {...field}
                  type={fieldName === PayPalFormInputNames.EmailAddress ? 'email' : 'text'}
                  size="base"
                  variant="default"
                  hasError={hasBlurred && !!error}
                  onBlur={() => setHasBlurred(true)}
                  value={value[fieldName]}
                  onValueChange={fieldValue => {
                    const newValue = {
                      ...value,
                      [fieldName]: fieldValue,
                    };
                    const isFieldValid = customValidateForm(newValue, fieldName);
                    const validValue =
                      fieldName === PayPalFormInputNames.EmailAddress
                        ? { isPayPalValueValid: isFieldValid }
                        : { isGoFundMeValueValid: isFieldValid };
                    onValueChange({ ...newValue, ...validValue });
                  }}
                />
                {hasBlurred && error && (
                  <div className={bem('validation-error', null, 'gik-form-group__error')}>{error}</div>
                )}
              </React.Fragment>
            );
          })}
        </>
      )}
      <div className={bem('description')}>{description}</div>
    </div>
  );
}
